import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteConfig, useNav } from '../../context/CMSContext';

const Header: React.FC = () => {
  const siteConfig = useSiteConfig();
  const nav = useNav();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const headerLight = true;

  return (
    <header
      className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-sm py-3"
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3">
          {siteConfig.logo && (
            <img src={siteConfig.logo} alt={siteConfig.name} className="h-12 md:h-[60px] w-auto object-contain" />
          )}
          <span className="text-[1.434375rem] font-bold tracking-wider">
            <span className={headerLight ? 'text-slate-900' : 'text-white'}>
              {siteConfig.nameEnglish.split(' ')[0]}
            </span>
            <span className="text-gold-600"> {siteConfig.nameEnglish.split(' ')[1] || ''}</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {nav.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium transition-colors hover:text-gold-600 ${
                isActive(link.href) ? 'text-gold-600' : headerLight ? 'text-slate-700' : 'text-white/90'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="bg-gold-600 hover:bg-gold-700 text-white px-5 py-2 rounded text-sm font-medium transition-all transform hover:-translate-y-0.5"
          >
            聯絡查詢
          </Link>
        </nav>

        {/* Mobile Toggle */}
        <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? (
            <X className={headerLight ? 'text-slate-900' : 'text-white'} />
          ) : (
            <Menu className={headerLight ? 'text-slate-900' : 'text-white'} />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="md:hidden absolute right-4 top-full mt-2 flex flex-col items-end gap-1 rounded-2xl bg-slate-900/80 p-3 backdrop-blur-md shadow-xl"
          >
            {nav.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block whitespace-nowrap px-4 py-2 text-base font-medium rounded-full text-white hover:bg-white/10 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="inline-block whitespace-nowrap mt-1 px-5 py-2 rounded-full bg-gold-600 hover:bg-gold-700 text-white font-semibold text-sm transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              聯絡查詢
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
